/**
 * PRAIRI Color System - Prairie Storm Palette
 * Evoking dynamic energy: emerald green for startups, electric blue for investors, and storm purple for innovation
 */

export const BRAND_COLORS = {
  // Primary Brand Colors - Prairie Storm
  primary: {
    // Grey-green tones - like muted growth and stability
    emerald: {
      50: '#f3fbea',
      100: '#e4f5d1',
      200: '#cbeaa9',
      300: '#addc7a',
      400: '#9ed562',
      500: '#92d050',
      600: '#70af2c',
      700: '#598c21',
    },
    // Grey-blue tones - like muted energy and dynamism
    electric: {
      50: '#f1f5f9', // Lightest grey-blue
      100: '#e2e8f0', // Soft grey-blue glow
      200: '#cbd5e1', // Gentle grey-blue
      300: '#94a3b8', // Warm grey-blue
      400: '#64748b', // Bright grey-blue
      500: '#475569', // Rich grey-blue
      600: '#334155', // Deep grey-blue
      700: '#1e293b', // Grey-blue storm
    },
    // Storm purple - like innovation and sophistication
    storm: {
      50: '#faf5ff', // Lightest storm
      100: '#f3e8ff', // Soft storm glow
      200: '#e9d5ff', // Gentle storm
      300: '#d8b4fe', // Warm storm
      400: '#c084fc', // Bright storm
      500: '#a855f7', // Rich storm purple
      600: '#9333ea', // Deep storm
      700: '#7c3aed', // Storm innovation
    },
  },

  // User Type Colors - Prairie Storm Themed
  userTypes: {
    startup: {
      primary: '#92d050', // emerald-500
      hover: '#70af2c', // emerald-600
      light: '#9ed562', // emerald-400
      border: '#9ed562', // emerald-400
      shadow: '#92d050', // emerald-500
      emoji: '🌿',
      meaning: 'Muted Growth & Stability',
    },
    investor: {
      primary: '#475569', // electric-500 - Muted energy
      hover: '#334155', // electric-600 - Rich muted energy
      light: '#64748b', // electric-400 - Light muted energy
      border: '#64748b', // electric-400 - Muted energy border
      shadow: '#475569', // electric-500 - Muted energy shadow
      emoji: '⚡',
      meaning: 'Muted Energy & Innovation',
    },
  },

  // Neutral Colors - Prairie Light Theme
  neutral: {
    // Light prairie tones for backgrounds
    light: {
      50: '#ffffff', // Pure white
      100: '#fefefe', // Warm white
      200: '#fafaf9', // Soft prairie light
      300: '#f5f5f4', // Gentle prairie light
      400: '#e7e5e4', // Subtle prairie shadow
      500: '#d6d3d1', // Light prairie shadow
    },
    // Dark earth tones for text and accents
    dark: {
      600: '#a8a29e', // Medium earth
      700: '#78716c', // Rich earth
      800: '#57534e', // Deep earth
      900: '#44403c', // Darker earth
      950: '#292524', // Deepest earth
    },
    white: '#ffffff',
  },

  // Gradient Combinations - Prairie Storm Theme
  gradients: {
    startup: 'from-emerald-400 to-emerald-600',
    investor: 'from-electric-400 to-electric-600',
    brand: 'from-emerald-400 to-electric-400',
    background: 'from-light-50 via-light-100 to-light-200',
    emerald: 'from-emerald-200 via-emerald-300 to-emerald-400',
    electric: 'from-electric-200 via-electric-300 to-electric-400',
    storm: 'from-storm-200 via-storm-300 to-storm-400',
    light: 'from-light-50 to-light-200',
  },
} as const;

// Helper function to get user type colors
export const getUserTypeColors = (userType: 'startup' | 'investor') => {
  return BRAND_COLORS.userTypes[userType];
};

/** Filled action buttons: muted olive, not bright lime — white text stays readable. */
export const FILTERED_GREEN_BUTTON =
  'bg-emerald-700 hover:bg-emerald-800 text-white focus:ring-emerald-700';

export { PRAIRI_SURFACES, PRAIRI_CATALOG_LABEL } from './prairiSurfaces';
export type { PrairiSurface } from './prairiSurfaces';

// CSS Class generators
export const getButtonClasses = (userType: 'startup' | 'investor') => {
  return {
    base: `bg-${userType === 'startup' ? 'emerald' : 'electric'}-500 hover:bg-${userType === 'startup' ? 'emerald' : 'electric'}-600`,
    border: `border-${userType === 'startup' ? 'emerald' : 'electric'}-400`,
    shadow: `hover:shadow-${userType === 'startup' ? 'emerald' : 'electric'}-500/30`,
  };
};
